import type {
    DataTableColumn,
    FilterItemConfig,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import { CONTRACT_STATUS } from '@/modules/Contract/constants'
import { TFunction } from 'i18next'
import type { PartnerContract } from '../types'

// ============================================================================
// Status Options
// ============================================================================

const getContractStatusOptions = (t: TFunction): OptionItem[] =>
    CONTRACT_STATUS(t).map((item) => ({
        label: item.label,
        value: item.value,
        color: item.color as OptionItem['color'],
    }))

// ============================================================================
// Column Configuration
// ============================================================================

export const createPartnerContractColumns = (
    t: TFunction
): DataTableColumn<PartnerContract>[] => [
    {
        key: 'contract_id',
        title: t('contract_id'),
        width: 130,
        sortable: true,
        pinned: 'left',
        formatter: createInternalLinkFormatter({
            pathKey: 'partnerContractDetail',
            pathParams: (value) => ({ id: value }),
            label: (value) => `#${value}`,
        }),
    },
    {
        key: 'contract_number_id',
        title: t('contract_number_id'),
        width: 200,
        sortable: true,
    },
    {
        key: 'partner_name',
        title: t('supplier'),
        width: 200,
    },
    {
        key: 'contract_key',
        title: t('contract_key'),
        width: 150,
        sortable: true,
    },
    {
        key: 'contract_value',
        title: t('contract_value'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: 'đ' }),
    },
    {
        key: 'contract_status',
        title: t('contract_status'),
        width: 160,
        sortable: true,
        formatter: createOptionsFormatter({
            options: getContractStatusOptions(t),
        }),
    },
    {
        key: 'contract_effective_date',
        title: t('contract_effective_date'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'contract_expiry_date',
        title: t('contract_expiry_date'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
    {
        key: 'employee_name',
        title: t('created_by'),
        width: 180,
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 150,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

export const createPartnerContractFilters = (
    t: TFunction
): FilterItemConfig[] => [
    {
        name: 'contract_status',
        type: 'multi-select',
        label: t('contract_status'),
        options: CONTRACT_STATUS(t).map((status) => ({
            value: status.value,
            label: status.label,
        })),
        placeholder: t('select-status'),
    },
    {
        name: 'contract_effective_date',
        type: 'date-range',
        label: t('contract_effective_date'),
        placeholder: t('select-date-range'),
    },
    {
        name: 'contract_expiry_date',
        type: 'date-range',
        label: t('contract_expiry_date'),
        placeholder: t('select-date-range'),
    },
]

// ============================================================================
// Constants
// ============================================================================

export const PARTNER_CONTRACT_ALLOWED_SORT_FIELDS = [
    'contract_id',
    'contract_number_id',
    'contract_key',
    'contract_value',
    'contract_status',
    'contract_effective_date',
    'contract_expiry_date',
    'created_at',
]

export interface PartnerContractFilters {
    contract_status: string[]
    contract_effective_date: string[]
    contract_expiry_date: string[]
}

export const PARTNER_CONTRACT_FILTER_DEFAULTS: PartnerContractFilters = {
    contract_status: [],
    contract_effective_date: [],
    contract_expiry_date: [],
}
